import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { X, UserPlus, Search, Trash2, Pencil, Check, Upload } from "lucide-react";
import { adicionarParticipantes, removerParticipante, renomearConversa, uploadFotoGrupo, removerFotoGrupo, ConversaResumo } from "../../services/chat";
import { GrupoAvatar } from "../../components/GrupoAvatar";
import { listarUsuariosAtivos } from "../../services/usuarios";
import { useAuth } from "../../hooks/useAuth";
import { useConfirm } from "../../components/ConfirmProvider";
import { useEscape } from "../../hooks/useEscape";

export function ParticipantesModal({ conversa, onClose }: { conversa: ConversaResumo; onClose: () => void }) {
  const { user, can } = useAuth();
  const qc = useQueryClient();
  const confirm = useConfirm();
  const [showAdd, setShowAdd] = useState(false);
  const [busca, setBusca] = useState("");
  const [marcados, setMarcados] = useState<string[]>([]);
  const [editandoNome, setEditandoNome] = useState(false);
  const [novoNome, setNovoNome] = useState(conversa.nome || "");
  // ESC desce na hierarquia: rename → add → modal inteiro.
  useEscape(true, () => {
    if (editandoNome) {
      setEditandoNome(false);
      setNovoNome(conversa.nome || "");
    } else if (showAdd) {
      setShowAdd(false);
      setMarcados([]);
    } else {
      onClose();
    }
  });

  const podeRenomear = (can(["gestor", "admin"])) && conversa.tipo === "grupo";

  const renomearM = useMutation({
    mutationFn: () => renomearConversa(conversa.id, novoNome.trim()),
    onSuccess: () => {
      setEditandoNome(false);
      qc.invalidateQueries({ queryKey: ["chat-conversa", conversa.id] });
      qc.invalidateQueries({ queryKey: ["chat-conversas"] });
    },
  });

  const { data: usuarios = [] } = useQuery({ queryKey: ["usuarios-ativos"], queryFn: listarUsuariosAtivos, enabled: showAdd });

  const meAdmin = useMemo(() => {
    const me = conversa.participantes.find((p) => p.usuarioId === user?.id);
    return me?.papel === "admin" || can(["admin"]);
  }, [conversa, user?.id, can]);

  const candidatos = useMemo(() => {
    const ja = new Set(conversa.participantes.map((p) => p.usuarioId));
    const t = busca.trim().toLowerCase();
    return usuarios
      .filter((u) => !ja.has(u.id) && u.perfil !== "cliente")
      .filter((u) => !t || u.nome.toLowerCase().includes(t) || u.email.toLowerCase().includes(t));
  }, [usuarios, conversa, busca]);

  const addM = useMutation({
    mutationFn: () => adicionarParticipantes(conversa.id, marcados),
    onSuccess: () => {
      setMarcados([]);
      setShowAdd(false);
      qc.invalidateQueries({ queryKey: ["chat-conversa", conversa.id] });
      qc.invalidateQueries({ queryKey: ["chat-conversas"] });
    },
  });

  const removeM = useMutation({
    mutationFn: (id: string) => removerParticipante(conversa.id, id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["chat-conversa", conversa.id] });
      qc.invalidateQueries({ queryKey: ["chat-conversas"] });
    },
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-md max-h-[88vh] flex flex-col shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-slate-200">
          <div className="flex items-center justify-between gap-2">
            <h3 className="font-semibold text-slate-800">Participantes ({conversa.participantes.length})</h3>
            <button onClick={onClose} className="p-1.5 rounded hover:bg-slate-100"><X size={18} /></button>
          </div>
          {conversa.tipo === "grupo" && (
            <div className="mt-2">
              {editandoNome ? (
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    autoFocus
                    value={novoNome}
                    onChange={(e) => setNovoNome(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && novoNome.trim().length >= 2) renomearM.mutate();
                    }}
                    className="flex-1 px-3 py-1.5 rounded-lg border border-blue-300 text-sm outline-none focus:border-blue-500"
                    placeholder="Nome do grupo"
                  />
                  <button
                    onClick={() => renomearM.mutate()}
                    disabled={novoNome.trim().length < 2 || renomearM.isPending}
                    className="p-1.5 rounded bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50"
                    title="Salvar"
                  >
                    <Check size={14} />
                  </button>
                  <button
                    onClick={() => { setEditandoNome(false); setNovoNome(conversa.nome || ""); }}
                    className="p-1.5 rounded text-slate-500 hover:bg-slate-100"
                    title="Cancelar"
                  >
                    <X size={14} />
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <p className="text-sm text-slate-700 flex-1 truncate">
                    <span className="text-xs text-slate-500">Nome do grupo:</span> <strong>{conversa.nome || "(sem nome)"}</strong>
                  </p>
                  {podeRenomear && (
                    <button
                      onClick={() => setEditandoNome(true)}
                      className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-slate-100 rounded"
                      title="Renomear grupo"
                    >
                      <Pencil size={14} />
                    </button>
                  )}
                </div>
              )}
              {(renomearM.error as any)?.response?.data?.error?.message && (
                <p className="text-xs text-red-600 mt-1">{(renomearM.error as any).response.data.error.message}</p>
              )}

              {/* Imagem do grupo — admin do grupo pode trocar/remover. */}
              {conversa.tipo === "grupo" && (
                <FotoGrupoEditor conversa={conversa} podeEditar={meAdmin} />
              )}
            </div>
          )}
        </div>

        {!showAdd ? (
          <>
            <div className="flex-1 overflow-y-auto p-2">
              {conversa.participantes.map((p) => (
                <div key={p.id} className="flex items-center gap-3 px-3 py-2 hover:bg-slate-50 rounded-lg">
                  <div className="w-9 h-9 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-sm font-bold">
                    {p.usuario.nome.charAt(0)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">
                      {p.usuario.nome}
                      {p.usuarioId === user?.id && <span className="ml-1 text-[10px] text-blue-600">(você)</span>}
                    </p>
                    <p className="text-[11px] text-slate-500 truncate">{p.usuario.email} · {p.usuario.perfil}{p.papel === "admin" ? " · admin do grupo" : ""}</p>
                  </div>
                  {(meAdmin || p.usuarioId === user?.id) && conversa.tipo !== "projeto" && (
                    <button
                      onClick={async () => {
                        const ok = await confirm({
                          title: p.usuarioId === user?.id ? "Sair do grupo" : `Remover ${p.usuario.nome}`,
                          message: p.usuarioId === user?.id ? "Você sairá deste grupo." : "Tem certeza?",
                          danger: true,
                          confirmText: p.usuarioId === user?.id ? "Sair" : "Remover",
                        });
                        if (ok) removeM.mutate(p.usuarioId);
                      }}
                      className="text-slate-400 hover:text-red-500"
                      title={p.usuarioId === user?.id ? "Sair" : "Remover"}
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              ))}
            </div>
            {meAdmin && (
              <div className="px-4 py-3 border-t border-slate-200 flex justify-end">
                <button onClick={() => setShowAdd(true)} className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded text-sm">
                  <UserPlus size={14} /> Adicionar
                </button>
              </div>
            )}
          </>
        ) : (
          <>
            <div className="px-4 pt-3">
              <div className="relative mb-2">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                <input
                  type="text"
                  value={busca}
                  onChange={(e) => setBusca(e.target.value)}
                  placeholder="Buscar pessoa…"
                  className="w-full pl-9 pr-3 py-2 rounded-lg border border-slate-200 text-sm outline-none focus:border-blue-500"
                />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-2">
              {candidatos.length === 0 && <p className="text-sm text-slate-400 text-center py-6">Nenhum candidato</p>}
              {candidatos.map((u) => {
                const sel = marcados.includes(u.id);
                return (
                  <button
                    key={u.id}
                    onClick={() => setMarcados((m) => sel ? m.filter((x) => x !== u.id) : [...m, u.id])}
                    className={`w-full text-left px-3 py-2 hover:bg-slate-50 rounded-lg flex items-center gap-3 ${sel ? "bg-blue-50" : ""}`}
                  >
                    <div className="w-9 h-9 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-sm font-bold">
                      {u.nome.charAt(0)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-800 truncate">{u.nome}</p>
                      <p className="text-[11px] text-slate-500 truncate">{u.email}</p>
                    </div>
                    {sel && <span className="text-blue-600 text-xs">✓</span>}
                  </button>
                );
              })}
            </div>
            <div className="px-4 py-3 border-t border-slate-200 flex justify-end gap-2">
              <button onClick={() => { setShowAdd(false); setMarcados([]); }} className="px-3 py-1.5 rounded text-sm bg-slate-100 hover:bg-slate-200">Cancelar</button>
              <button onClick={() => addM.mutate()} disabled={marcados.length === 0 || addM.isPending} className="px-4 py-1.5 rounded text-sm bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50">
                {addM.isPending ? "Adicionando…" : `Adicionar (${marcados.length})`}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function FotoGrupoEditor({ conversa, podeEditar }: { conversa: ConversaResumo; podeEditar: boolean }) {
  const qc = useQueryClient();
  const [enviando, setEnviando] = useState(false);

  async function aoSelecionar(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    setEnviando(true);
    try {
      await uploadFotoGrupo(conversa.id, f);
      qc.invalidateQueries({ queryKey: ["chat-conversa", conversa.id] });
      qc.invalidateQueries({ queryKey: ["chat-conversas"] });
    } catch (err) {
      console.error(err);
      alert("Falha no upload.");
    } finally {
      setEnviando(false);
      e.target.value = "";
    }
  }

  async function aoRemover() {
    setEnviando(true);
    try {
      await removerFotoGrupo(conversa.id);
      qc.invalidateQueries({ queryKey: ["chat-conversa", conversa.id] });
      qc.invalidateQueries({ queryKey: ["chat-conversas"] });
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className="mt-3 flex items-center gap-3">
      <GrupoAvatar fotoUrl={conversa.fotoUrl} tamanho={48} titulo={conversa.nome || undefined} />
      <div className="flex-1 min-w-0">
        <p className="text-xs text-slate-500 mb-1">Imagem do grupo</p>
        {podeEditar ? (
          <div className="flex items-center gap-2 flex-wrap">
            <label className="inline-flex items-center gap-1.5 cursor-pointer bg-white hover:bg-slate-50 border border-slate-300 text-slate-700 text-xs px-2.5 py-1.5 rounded-lg">
              <Upload size={12} /> {conversa.fotoUrl ? "Trocar" : "Subir imagem"}
              <input type="file" accept="image/*" className="hidden" onChange={aoSelecionar} disabled={enviando} />
            </label>
            {conversa.fotoUrl && (
              <button
                type="button"
                onClick={aoRemover}
                disabled={enviando}
                className="inline-flex items-center gap-1 text-red-600 hover:bg-red-50 border border-red-200 text-xs px-2 py-1.5 rounded-lg disabled:opacity-50"
              >
                <Trash2 size={12} /> Remover
              </button>
            )}
          </div>
        ) : (
          <p className="text-[11px] text-slate-400">Só admin do grupo pode trocar a imagem.</p>
        )}
      </div>
    </div>
  );
}
